import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Music, Sparkles } from "lucide-react";

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_dc1f59b0-d4ed-4a52-be65-0a28e94c6aa3/artifacts/1mrurg75_image.png";

const lines = [
  { text: "STOP IN.", color: "text-[#0A0A0A]" },
  { text: "TASTE LIFE.", color: "text-[#FF2A54]" },
  { text: "STAY SWEET.", color: "text-[#0A0A0A]" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative border-b-2 border-[#0A0A0A]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-5 pb-10 pt-8 sm:px-8 lg:grid-cols-[1.2fr_1fr] lg:pt-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-eyebrow"
            className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest"
          >
            <Music className="h-3.5 w-3.5 text-[#FF2A54]" /> Candy &amp; toys, shipped across Canada
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className={`block ${line.color}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            data-testid="hero-hook"
            className="mt-4 max-w-md text-sm font-medium leading-relaxed text-[#4A4A4A] sm:text-base"
          >
            Canada&rsquo;s loudest little candy &amp; toy shop — trends, fun, and treats hand-packed and shipped to your door.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.95 }}
            className="mt-6 flex flex-wrap items-center gap-4"
          >
            <a
              href="#shop"
              data-testid="hero-shop-trending-button"
              className="hard-shadow-lg hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-white"
            >
              Shop Trending <ArrowDown className="h-4 w-4" />
            </a>
            <a
              href="#new-drops"
              data-testid="hero-new-drops-button"
              className="hard-shadow hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-[#0A0A0A]"
            >
              New Drops
            </a>
          </motion.div>
        </div>

        <div className="relative hidden lg:block lg:max-w-sm lg:justify-self-end">
          <motion.div
            style={{ y: yLogo }}
            initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="hard-shadow-lg relative z-10 overflow-hidden rounded-2xl border-2 border-[#0A0A0A] bg-white"
          >
            <img src={LOGO_URL} alt="Canadian Beats Marketplace banner" data-testid="hero-brand-image" className="w-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, rotate: -20 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="hard-shadow absolute -right-4 -top-6 z-20 flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-4 py-2 font-display text-xs font-extrabold uppercase"
          >
            <Sparkles className="h-4 w-4" /> Fresh drops weekly
          </motion.div>
        </div>
      </div>
    </section>
  );
}
